/** Browser-side Jarvis attachments: shrink big photos before upload, then POST each file. */
import { api, API_BASE } from "./api";
import type { JarvisAttachment } from "./jarvis";

const LONG_EDGE = 2000;
const SHRINK_OVER = 1.5 * 1024 * 1024;

/** A photo or screenshot over about 1.5 MB or 2000 px is redrawn as a JPEG that fits; GIFs and small images stay as they are. */
export async function shrinkImage(file: File): Promise<File> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return file;
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file); } catch { return file; }
  const scale = Math.min(1, LONG_EDGE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= SHRINK_OVER) { bitmap.close(); return file; }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
  const g = canvas.getContext("2d");
  if (!g) { bitmap.close(); return file; }
  g.fillStyle = "#fff"; g.fillRect(0, 0, canvas.width, canvas.height); // transparent screenshots stay readable as JPEG
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height); bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.86));
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], file.name.replace(/\.(png|webp|jpe?g)$/i, "") + ".jpg", { type: "image/jpeg" });
}

export async function uploadAttachment(file: File, signal?: AbortSignal): Promise<JarvisAttachment> {
  const body = new FormData();
  body.append("file", await shrinkImage(file));
  return api<JarvisAttachment>("/api/jarvis/attachments", { method: "POST", body, signal });
}

export const attachmentUrl = (id: number) => `${API_BASE}/api/jarvis/attachments/${id}`;
